import {
  BrandAssetApiPath,
  BrandAssetDto,
  BrandAssetLibrary,
  BrandAssetNameMaxLength,
  BrandAssetUpdateRequest,
  BrandAssetUploadField,
  BrandKitActiveResponse,
  BrandKitApiPath,
  BrandKitDraftRef,
  BrandKitPublishRequest,
  BrandKitSaveDraftRequest,
  BrandKitVersionDto,
  BrandKitVersionSummaries,
} from "@arrowsup/platform-dtos";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosInstance } from "axios";
import { useContext } from "react";
import * as ss from "superstruct";
import { ApiContext } from "../api/api-context";
import { useEffectiveOrgId } from "../auth/use-effective-org-id";

const validated = <T>(data: unknown, struct: ss.Struct<T, unknown>): T => {
  const [err, value] = ss.validate(data, struct);
  if (err) {
    throw new Error(`Invalid brand kit response: ${err.message}`);
  }
  return value;
};

const getUrl = async <T>(
  api: AxiosInstance,
  url: string,
  struct: ss.Struct<T, unknown>,
  signal: AbortSignal
): Promise<T> => {
  const resp = await api.get<unknown>(url, { signal });
  return validated(resp.data, struct);
};

const get = <T>(
  api: AxiosInstance,
  path: string,
  struct: ss.Struct<T, unknown>,
  signal: AbortSignal
): Promise<T> => getUrl(api, `${BrandKitApiPath}${path}`, struct, signal);

/**
 * Asset links expire after an hour, so data holding them is refetched every
 * half hour, even on a page left open.
 */
const assetLinksRefresh = {
  staleTime: 30 * 60 * 1000,
  refetchInterval: 30 * 60 * 1000,
};

/** Every brand kit query key starts with this, so one invalidation covers all. */
const useBrandKitKey = () => ["brand-kit", useEffectiveOrgId()] as const;

export const useActiveBrandKit = () => {
  const { api } = useContext(ApiContext);
  const key = useBrandKitKey();
  return useQuery({
    queryKey: [...key, "active"],
    ...assetLinksRefresh,
    // Cache the whole response: React Query rejects undefined query data, and
    // `version` is absent until something is published.
    queryFn: ({ signal }) => get(api, "", BrandKitActiveResponse, signal),
    select: (resp) => resp.version,
  });
};

export const useBrandKitVersions = () => {
  const { api } = useContext(ApiContext);
  const key = useBrandKitKey();
  return useQuery({
    queryKey: [...key, "versions"],
    queryFn: ({ signal }) =>
      get(api, "/versions", BrandKitVersionSummaries, signal),
  });
};

export const useBrandKitVersion = (versionNumber?: number) => {
  const { api } = useContext(ApiContext);
  const key = useBrandKitKey();
  const path = `/versions/${versionNumber ?? ""}`;
  return useQuery({
    queryKey: [...key, "version", versionNumber],
    queryFn: ({ signal }) => get(api, path, BrandKitVersionDto, signal),
    enabled: versionNumber !== undefined,
    ...assetLinksRefresh,
  });
};

export const useBrandKitDraft = () => {
  const { api } = useContext(ApiContext);
  const key = useBrandKitKey();
  return useQuery({
    queryKey: [...key, "draft"],
    ...assetLinksRefresh,
    queryFn: ({ signal }) => get(api, "/draft", BrandKitVersionDto, signal),
  });
};

/** Invalidates every brand kit query; rejects if refetching one in use fails. */
export const useRefreshBrandKit = () => {
  const queryClient = useQueryClient();
  const key = useBrandKitKey();
  return () =>
    queryClient.invalidateQueries({ queryKey: key }, { throwOnError: true });
};

/** Mutations on the draft. Each one refreshes every brand kit query. */
export const useBrandKitDraftMutations = () => {
  const { api } = useContext(ApiContext);
  const queryClient = useQueryClient();
  const key = useBrandKitKey();

  const send = async (
    method: "put" | "post",
    path: string,
    body: unknown
  ): Promise<BrandKitVersionDto> => {
    const url = `${BrandKitApiPath}${path}`;
    const resp =
      method === "put"
        ? await api.put<unknown>(url, body)
        : await api.post<unknown>(url, body);
    return validated(resp.data, BrandKitVersionDto);
  };

  const onSuccess = () => queryClient.invalidateQueries({ queryKey: key });

  const save = useMutation({
    mutationFn: (req: BrandKitSaveDraftRequest) => send("put", "/draft", req),
    onSuccess: (draft) => {
      queryClient.setQueryData([...key, "draft"], draft);
      // Which assets the draft uses may have changed.
      void queryClient.invalidateQueries({ queryKey: [...key, "assets"] });
    },
  });
  const publish = useMutation({
    mutationFn: (req: BrandKitPublishRequest) =>
      send("post", "/draft/publish", req),
    onSuccess,
  });
  const discard = useMutation({
    mutationFn: (ref: BrandKitDraftRef) => send("post", "/draft/discard", ref),
    onSuccess,
  });
  const restore = useMutation({
    mutationFn: ({
      versionNumber,
      ref,
    }: {
      versionNumber: number;
      ref: BrandKitDraftRef;
    }) => send("post", `/versions/${versionNumber}/restore`, ref),
    onSuccess,
  });

  return { save, publish, discard, restore };
};

/** The org's asset library. Only for users who can update the kit. */
export const useBrandAssetLibrary = () => {
  const { api } = useContext(ApiContext);
  const key = useBrandKitKey();
  return useQuery({
    queryKey: [...key, "assets"],
    queryFn: ({ signal }) =>
      getUrl(api, BrandAssetApiPath, BrandAssetLibrary, signal),
    ...assetLinksRefresh,
  });
};

/** Changes to the library. Each one refreshes the queries it affects. */
export const useBrandAssetMutations = () => {
  const { api } = useContext(ApiContext);
  const queryClient = useQueryClient();
  const key = useBrandKitKey();
  const refreshLibrary = () =>
    queryClient.invalidateQueries({ queryKey: [...key, "assets"] });

  const upload = useMutation({
    mutationFn: async (file: File): Promise<BrandAssetDto> => {
      const form = new FormData();
      // Sent separately, as multipart file names lose non-ASCII characters.
      const name = file.name.trim().slice(0, BrandAssetNameMaxLength);
      if (name) {
        form.append("name", name);
      }
      form.append(BrandAssetUploadField, file);
      const resp = await api.post<unknown>(BrandAssetApiPath, form);
      return validated(resp.data, BrandAssetDto);
    },
    onSuccess: refreshLibrary,
  });
  const update = useMutation({
    mutationFn: async ({
      id,
      ...req
    }: BrandAssetUpdateRequest & { id: string }): Promise<BrandAssetDto> => {
      const resp = await api.patch<unknown>(`${BrandAssetApiPath}/${id}`, req);
      return validated(resp.data, BrandAssetDto);
    },
    // Versions show asset names too.
    onSuccess: () => queryClient.invalidateQueries({ queryKey: key }),
  });
  const remove = useMutation({
    mutationFn: async (id: string): Promise<void> => {
      await api.delete(`${BrandAssetApiPath}/${id}`);
    },
    onSuccess: refreshLibrary,
  });

  return { upload, update, remove };
};
